import { DatePipe } from '@angular/common';
import { ChangeDetectionStrategy,Component, inject, OnDestroy, OnInit, ViewChild } from '@angular/core';
import { FormsModule, NgForm } from '@angular/forms';
import { MatAutocomplete, MatAutocompleteTrigger } from '@angular/material/autocomplete';
import { MatButton, MatIconButton } from '@angular/material/button';
import { MatChip, MatChipListbox, MatChipOption, MatChipRemove, MatChipSet } from '@angular/material/chips';
import { MatOption } from '@angular/material/core';
import { MatDialog } from '@angular/material/dialog';
import { MatFormField, MatLabel } from '@angular/material/form-field';
import { MatIcon } from '@angular/material/icon';
import { MatInput } from '@angular/material/input';
import { MatSnackBar } from '@angular/material/snack-bar';
import { MatTooltip } from '@angular/material/tooltip';
import { ActivatedRoute, Router, RouterLink } from '@angular/router';
import { forkJoin } from 'rxjs';

import { AdminComponentBase, PageSimpleContentComponent } from 'app/layout/content';
import { AuthorizationContext, AuthorizationService, AuthorizedUsersService } from 'app/shared/authentication';
import { ErrorComponent } from 'app/shared/components/error.component';
import { ConfirmDialogService } from 'app/shared/dialog/confirm-dialog-service';
import { AvailableRoles, DownloadPath, GlobalPermission, GlobalRole, UserRoles } from 'app/shared/graph';
import { AddAuthorizedUserDialogComponent, AddAuthorizedUserDialogData, AddAuthorizedUserDialogResult } from 'app/shared/permission/add-authorized-user-dialog.component';
import { CopyToClipboardComponent } from 'app/shared/utilities/copy-to-clipboard.component';
import { FromNowPipe } from 'app/shared/utilities/from-now.pipe';

@Component({
    templateUrl: './admin-user-page.component.html',
    styleUrls: ['./admin-user-page.component.scss'],
    changeDetection: ChangeDetectionStrategy.Eager,
    imports: [PageSimpleContentComponent, ErrorComponent, CopyToClipboardComponent, MatChipSet, MatChip, MatChipRemove, MatIcon, FormsModule, MatFormField, MatLabel, MatInput, MatAutocompleteTrigger, MatAutocomplete, MatOption, MatButton, DatePipe, FromNowPipe, MatTooltip, RouterLink, MatChipListbox, MatChipOption, MatIconButton]
})
export class AdminUserPageComponent extends AdminComponentBase implements OnInit, OnDestroy {
    private readonly route = inject(ActivatedRoute);
    private readonly router = inject(Router);
    private readonly dialog = inject(MatDialog);
    private readonly snackBar = inject(MatSnackBar);
    private readonly authorizedUsersService = inject(AuthorizedUsersService);
    private readonly authorizationService = inject(AuthorizationService);
    private readonly confirmDialogService = inject(ConfirmDialogService);

    userEmail: string;
    user?: UserRoles;
    authorizationContext: AuthorizationContext;
    availableRoles: AvailableRoles;
    availableDownloads: DownloadPath[];
    downloadValue: string;
    @ViewChild('form')
    form: NgForm;

    ngOnInit() {
        this.registerSubscription(this.authorizationService.authorizationContext().subscribe(authorizationContext => {
            this.authorizationContext = authorizationContext;
        }));
        this.registerSubscription(this.route.params.subscribe(params => {
            this.userEmail = params.userEmail;

            this.subscribeWithGenericLoadingErrorHandling(forkJoin([
                this.authorizedUsersService.getUser(this.userEmail),
                this.authorizedUsersService.getAvailableRoles(),
                this.authorizedUsersService.getAvailableDownloadPaths()
            ], (user, availableRoles, availableDownloads) => {
                this.user = user;
                this.availableRoles = availableRoles;
                this.availableDownloads = [...availableDownloads, { path: '*' }];
            }));
        }));
    }

    ngOnDestroy() {
        this.unsubscribeSubscriptions();
    }

    canManageUser(): boolean {
        return this.authorizationContext.hasGlobalPermissions(GlobalPermission.ManageAdminUsers);
    }

    // region Global role

    openAddGlobalRole(user: UserRoles) {
        const dialogRef = this.dialog.open<AddAuthorizedUserDialogComponent<GlobalRole>, AddAuthorizedUserDialogData<GlobalRole>, AddAuthorizedUserDialogResult<GlobalRole>>(
            AddAuthorizedUserDialogComponent, {
                data: {
                    email: user.user.email,
                    target: {
                        availableRoles: this.availableRoles.globalRoles.filter(role => !user || !user.globalRoles.includes(role)),
                        label: user.user.email
                    }
                }
            });
        dialogRef.afterClosed().subscribe(result => {
            if (!result) {
                return;
            }
            this.subscribeWithGenericSavingErrorHandling(this.authorizedUsersService.grantUserRole({
                email: result.email,
                global: [result.role]
            }), () => {
                this.user?.globalRoles.push(result.role);
            });
        });
    }


    removeGlobalRole(user: UserRoles, role: GlobalRole) {
        this.confirmDialogService.confirm('Remove role', `Remove the role <code>${role}</code> from the user <code>${user.user.email}</code>?`, undefined, {
            yes: {
                label: 'Remove',
                warn: true
            }
        }).subscribe(() => {
            this.subscribeWithGenericSavingErrorHandling(this.authorizedUsersService.revokeUserRole({
                email: user.user.email,
                global: [role]
            }), () => {
                let roleIndex = user.globalRoles.indexOf(role);
                if (roleIndex !== -1) {
                    user.globalRoles.splice(roleIndex, 1);
                }
            });
        });
    }

    // endregion

    removeDownload(user: UserRoles, download: string) {
        this.confirmDialogService.confirm('Remove download', `Remove permission to download <code>${download}</code> from the user <code>${user.user.email}</code>?`, undefined, {
            yes: {
                label: 'Remove',
                warn: true
            }
        }).subscribe(() => {
            this.subscribeWithGenericSavingErrorHandling(this.authorizedUsersService.revokeUserRole({
                email: user.user.email,
                downloads: [download]
            }), () => {
                let roleIndex = user.downloads.indexOf(download);
                if (roleIndex !== -1) {
                    user.downloads.splice(roleIndex, 1);
                }
            });
        });
    }

    addDownload(download: string) {
        this.subscribeWithGenericSavingErrorHandling(this.authorizedUsersService.grantUserRole({
            email: this.userEmail,
            downloads: [download]
        }), () => {
            this.user?.downloads.push(download);
        });
        this.form.resetForm();
    }

    filterDownloads(availableDownloads: DownloadPath[]) {
        if (!this.user) {
            return availableDownloads;
        }
        return availableDownloads.filter(d => !this.user?.downloads.includes(d.path));
    }

    hasAnyRole(user: UserRoles | undefined): boolean {
        if (user?.globalRoles && user.globalRoles.length > 0) {
            return true;
        }
        if (user?.subscriptionsRoles && user.subscriptionsRoles.length > 0) {
            return true;
        }
        if (user?.storesRoles && user.storesRoles.length > 0) {
            return true;
        }
        if (user?.librariesRoles && user.librariesRoles.length > 0) {
            return true;
        }
        if (user?.fulfillersRoles && user.fulfillersRoles.length > 0) {
            return true;
        }
        return false;
    }

    deleteUser(user: UserRoles | undefined) {
        let email = user?.user.email;
        if (!email) {
            return;
        }

        this.confirmDialogService.confirm('Delete User', `Delete the user <code>${email}</code> ?`, undefined, {
            yes: {
                label: 'Delete user',
                warn: true
            }
        }).subscribe(() => {
            this.snackBar.open(`Admin user ${email} has been deleted`, undefined, { duration: 5000 });
            this.subscribeWithGenericSavingErrorHandling(this.authorizedUsersService.deleteAdminUser({
                email: email
            }), () => {
                this.router.navigate(['/admin-users']);
            });
        });
    }
}
